<template>
  <v-container fluid class="container-video pa-0">
    <div id="main">
      <video class="active-video" autoplay playsinline muted></video>
      <div id="localThumbnailPreview">
        <video autoplay playsinline muted></video>
      </div>

      <div class="btn-block-video">
        <div class="side-btn-block">
          <p class="room-name truncate-overflow ml-4 mb-0">
            {{ roomName }}
          </p>
        </div>
        <div>
          <ButtonDeviceActivation kind="audio" v-on:toggle-device="toggleDevice" :activeDevice="activeDevicesAudio" />
          <v-btn class="mx-2" @click="disconnect" fab outlined dark>
            <v-icon color="red">call_end</v-icon>
          </v-btn>
          <ButtonDeviceActivation kind="video" v-on:toggle-device="toggleDevice" :activeDevice="activeDevicesVideo" />
        </div>

        <div class="side-btn-block side-btn-block-right">
          <div class="share-btn mx-4">
            <v-btn class="disable-mobile mx-2" @click="shareScreenHandler" text dark>
              <v-icon>{{ !screenTrack ? 'present_to_all' : 'cancel_presentation' }}</v-icon>
            </v-btn>
            <p class="share-btn-label my-0">{{ $t('rooms.live.shareScreen') }}</p>
          </div>

          <SelectModalOptions :roomInformation="currentRoom" :authenticated="authenticated" />
        </div>
      </div>
    </div>

    <!-- Pariticipants -->
    <div class="open-participants-header">
      <v-btn dark text @click="openParticipants">
        <v-icon>people_outline</v-icon>
      </v-btn>
    </div>
    <div id="participants" class="hide-scrollbar">
      <div class="header-participants-controls">
        <v-btn text fab @click="closeParticipants">
          <v-icon>close</v-icon>
        </v-btn>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { connect, createLocalTracks, createLocalVideoTrack, LocalVideoTrack } from 'twilio-video'
import { connectOptions } from '@/script/connectOptions.js'
import { cssVideoCallHandler } from '@/mixins/cssVideoCallHandler.js'
import { callVideoActions } from '@/mixins/callVideoActions.js'

import ButtonDeviceActivation from './buttons/ButtonDeviceActivation.vue'
import SelectModalOptions from './SelectModalOptions.vue'

export default {
  name: 'LiveVideoCall',

  mixins: [cssVideoCallHandler, callVideoActions],

  components: {
    ButtonDeviceActivation,
    SelectModalOptions
  },

  props: {
    id_live_video: {
      type: String,
      default: ''
    },
    currentRoom: {
      type: Object
    },
    authenticated: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    ...mapGetters(['userId']),
    ...mapState({
      roomDevices: state => Object.assign({}, state.roomDevices),
      roomDevicesVideo: state => state.roomDevices.video,
      roomDevicesAudio: state => state.roomDevices.audio,
      activeDevices: state => state.activeDevices,
      activeDevicesAudio: state => state.activeDevices.audio,
      activeDevicesVideo: state => state.activeDevices.video
    }),

    roomName() {
      return this.currentRoom && this.currentRoom.session_name
    }
  },

  data() {
    return {
      room: null,
      activeParticipant: null,
      isActiveMenu: null,
      screenTrack: null,
      mainContainerVideo: null,
      thumbnailContainerVideo: null
    }
  },

  mounted() {
    // SetContainer
    this.mainContainerVideo = document.getElementById('main').getElementsByTagName('video')[0]
    this.thumbnailContainerVideo = document.getElementById('localThumbnailPreview').getElementsByTagName('video')[0]
  },

  async created() {
    window.onbeforeunload = () => {
      this.room && this.room.disconnect()
    }

    this.viewportMobile()
    window.addEventListener('resize', this.viewportMobile)

    this.eventParticipants()

    this.updateConnectOptions()

    const token = await this.getToken(this.userId, this.roomName)

    token && this.joinRoom(token)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.resizeScreen)
    window.removeEventListener('resize', this.viewportMobile)
    this.disconnect()
  },

  methods: {
    async getToken(userId, roomName) {
      return await this.$store.dispatch('getTokenRoomLive', {
        userId: userId,
        roomName: this.currentRoom.room_name
      })
    },

    updateConnectOptions() {
      // Add the specified Room name to ConnectOptions.
      connectOptions.name = this.currentRoom.room_name

      // Add the specified audio device ID to ConnectOptions.
      const audioDeviceId = this.activeDevices.audio && this.roomDevicesAudio
      connectOptions.audio = !audioDeviceId ? audioDeviceId : { deviceId: { exact: this.roomDevicesAudio } }

      // Add the specified video device ID to ConnectOptions.
      const videoDeviceId = this.activeDevices.video && this.roomDevicesVideo
      connectOptions.video = !videoDeviceId ? videoDeviceId : { deviceId: { exact: this.roomDevicesVideo } }
    },

    async joinRoom(token) {
      let room = null

      if (!token) {
        return
      }

      try {
        room = await connect(token, connectOptions)
      } catch (error) {
        this.$store.commit('SET_SNACKBAR', { snack: this.$t('rooms.roomConnexionFailed'), color: 'error' })
        return
      }

      this.room = room
      window.room = room

      // GA event for connection
      this.$mam.liveRoomConnection()

      // Handle the LocalParticipant's media.
      this.participantConnected(room.localParticipant, room)
      this.addLocalPreview(room.localParticipant, room)

      // Display again the local preview after unsubscribed
      room.localParticipant.on('trackPublished', () => {
        this.addLocalPreview(room.localParticipant, room)
      })

      // Subscribe to the media published by RemoteParticipants already in the Room.
      room.participants.forEach(participant => {
        this.participantConnected(participant, room)
      })

      // Subscribe to the media published by RemoteParticipants joining the Room later.
      room.on('participantConnected', participant => {
        this.$store.commit('SET_SNACKBAR', { snack: this.$t('rooms.participantJoined'), color: 'primary' })

        this.participantConnected(participant, room)
        this.setCurrentActiveParticipant(room)
      })

      // Handle a disconnected RemoteParticipant.
      room.on('participantDisconnected', participant => {
        this.$store.commit('SET_SNACKBAR', { snack: this.$t('rooms.participantLeaved'), color: 'primary' })
        this.participantDisconnected(participant, room)
      })

      // set the current active Participant
      this.setCurrentActiveParticipant(room)

      room.on('dominantSpeakerChanged', () => {
        if (!this.isActiveParticipantPinned) {
          this.setCurrentActiveParticipant(room)
        }
      })

      room.once('disconnected', room => {
        // Stop the LocalVideoTrack.
        const localParticipant = this.room.localParticipant
        const videoTracks = localParticipant.videoTracks
        const audioTracks = localParticipant.audioTracks

        if (videoTracks.size > 0) Array.from(videoTracks.values())[0].track.stop()
        if (audioTracks.size > 0) Array.from(audioTracks.values())[0].track.stop()

        // Handle the disconnected LocalParticipant.
        this.participantDisconnected(room.localParticipant, room)

        // Handle the disconnected RemoteParticipants.
        room.participants.forEach(participant => {
          this.participantDisconnected(participant, room)
        })

        // Reset container stream thumbnail and main video
        this.mainContainerVideo.srcObject = null
        this.thumbnailContainerVideo.srcObject = null
      })
    },

    participantConnected(participant, room) {
      // Set up the Participant's media container.
      this.setupParticipantContainer(participant, room)

      // Handle the TrackPublications already published by the Participant.
      participant.tracks.forEach(publication => {
        this.trackPublished(publication, participant)
      })

      // Handle theTrackPublications that will be published by the Participant later.
      participant.on('trackPublished', publication => {
        this.trackPublished(publication, participant)
      })
    },

    setActiveParticipant(participant, dominantSpeaker) {
      if (this.room.participants.size > 0) {
        if (dominantSpeaker) {
          participant = dominantSpeaker
        } else {
          participant = this.room.participants.values().next().value
        }
      }

      if (this.activeParticipant) {
        const activeParticipantContainer = document.getElementById(this.activeParticipant.sid)
        activeParticipantContainer.classList.remove('active')
        activeParticipantContainer.classList.remove('pinned')

        // Detach any existing VideoTrack of the active Participant.
        const { track: activeTrack } = Array.from(this.activeParticipant.videoTracks.values())[0] || {}
        if (activeTrack) {
          this.mainContainerVideo.style.opacity = '0'
          activeTrack.detach(this.mainContainerVideo)
        }
      }

      // Set the new active Participant.
      this.activeParticipant = participant
      const { identity, sid } = participant
      const participantContainer = document.getElementById(sid)

      participantContainer.classList.add('active')
      this.isActiveParticipantPinned && participantContainer.classList.add('pinned')

      // Attach the new active Participant's video.
      const { track } = Array.from(participant.videoTracks.values())[0] || {}
      if (track && this.activeDevices[track.kind]) {
        this.mainContainerVideo.style.opacity = ''
        track.attach(this.mainContainerVideo)
      }

      // Set the new active Participant's identity
      this.mainContainerVideo.setAttribute('data-identity', identity)
    },

    participantDisconnected(participant, room) {
      if (this.activeParticipant === participant || this.isActiveParticipantPinned) {
        this.isActiveParticipantPinned = false
        this.setCurrentActiveParticipant(room)
      }

      // Remove the Participant's media container.
      const participantContainer = document.getElementById(participant.sid)
      participantContainer.remove()
    },

    setCurrentActiveParticipant(room) {
      const { dominantSpeaker, localParticipant } = room
      this.setActiveParticipant(localParticipant, dominantSpeaker)
    },

    setupParticipantContainer(participant, room) {
      const { identity, sid } = participant

      // Add a container for the Participant's media.
      const container = `<div class="participant" data-identity="${identity}" id="${sid}">
    <audio autoplay ${participant === room.localParticipant ? 'muted' : ''} style="opacity: 0"></audio>
    <video autoplay muted playsinline style="width: 100%; opacity: 0;"></video>
  </div>`

      // Add the Participant's container to the DOM.
      const participantsContainer = document.getElementById('participants')
      participantsContainer.insertAdjacentHTML('beforeend', container)

      const containerInDom = document.getElementById(sid)
      const mic = document.createElement('img')
      const avatar = document.createElement('img')

      mic.classList.add('mic_avatar')
      mic.src = require('@/assets/mic_off.svg')

      avatar.classList.add('camera_avatar')
      avatar.src = require('@/assets/account.svg')

      containerInDom.appendChild(mic)
      containerInDom.appendChild(avatar)
    },

    addLocalPreview(localParticipant) {
      localParticipant.tracks.forEach(publication => {
        if (publication.kind === 'video') {
          const media = document.getElementById('localThumbnailPreview').getElementsByTagName('video')[0]
          media.style.opacity = ''
          publication.track.attach(media)
        }
      })
    },

    trackPublished(publication, participant) {
      // Especially LocalParticipant
      // If the TrackPublication is already subscribed to, then attach the Track to the DOM.
      if (publication.track) {
        if (this.activeDevices[publication.track.kind]) {
          this.attachTrack(publication.track, participant)
          this.enableTrack(participant, publication.track)
        } else {
          this.disabledTrack(participant, publication.track)
        }
      }

      // Especially remote participant
      // Once the TrackPublication is subscribed to, attach the Track to the DOM.
      publication.on('subscribed', track => {
        this.attachTrack(track, participant)
        this.enableTrack(participant, track)
      })

      // Once the TrackPublication is unsubscribed from, detach the Track from the DOM.
      publication.on('unsubscribed', track => {
        this.detachTrack(track, participant)
        this.disabledTrack(participant, track)
      })
    },

    attachTrack(track, participant) {
      // Attach the Participant's Track to the thumbnail.
      const media = document.getElementById(participant.sid).getElementsByTagName(track.kind)[0]

      media.style.opacity = ''
      track.attach(media)

      // If the attached Track is a VideoTrack that is published by the active
      // Participant, then attach it to the main video as well.
      if (track.kind === 'video' && participant === this.activeParticipant) {
        this.mainContainerVideo.style.opacity = ''
        track.attach(this.mainContainerVideo)
      }
    },

    /**
     * Detach a Track from the DOM.
     * @param track - the Track to be detached
     * @param participant - the Participant that is publishing the Track
     */
    detachTrack(track, participant) {
      // Detach the Participant's Track from the thumbnail.
      const media = document.getElementById(participant.sid).getElementsByTagName(track.kind)[0]
      media.style.opacity = '0'
      track.detach(media)

      // If the detached Track is a VideoTrack that is published by the active
      // Participant, then detach it from the main video as well.
      if (track.kind === 'video' && participant === this.activeParticipant) {
        this.mainContainerVideo.style.opacity = '0'
        track.detach(this.mainContainerVideo)
      }
    },

    disabledTrack(participant, track) {
      const container = document.getElementById(participant.sid)

      if (track && track.kind === 'audio') {
        const mic = document.createElement('img')
        mic.classList.add('mic_avatar')
        mic.src = require('@/assets/mic_off.svg')

        container.appendChild(mic)
      }

      if (track && track.kind === 'video') {
        const avatar = document.createElement('img')
        avatar.classList.add('camera_avatar')
        avatar.src = require('@/assets/account.svg')

        container.appendChild(avatar)
      }
    },

    enableTrack(participant, track) {
      const container = document.getElementById(participant.sid)

      if (track && track.kind === 'audio') {
        const avatar = container.querySelector('.mic_avatar')
        avatar && avatar.remove()
      }

      if (track && track.kind === 'video') {
        const avatar = container.querySelector('.camera_avatar')
        avatar && avatar.remove()
      }
    },

    async toggleDevice(kind) {
      if (!this.room) return

      if (this.activeDevices[kind]) {
        this.room.localParticipant.tracks.forEach(publication => {
          if (publication.kind === kind) {
            this.disabledTrack(this.room.localParticipant, publication.track)
            this.commitToggleDevice(kind, false)
            publication.track.stop()
            publication.unpublish()
          }
        })
      } else {
        if (this.screenTrack) {
          this.room.localParticipant.unpublishTrack(this.screenTrack)
          this.screenTrack.stop()
          this.screenTrack = null
        }

        const [track] = await createLocalTracks({ [kind]: true })

        this.commitToggleDevice(kind, true)
        this.enableTrack(this.room.localParticipant, track)
        return this.room.localParticipant.publishTrack(track)
      }
    },

    async shareScreenHandler() {
      if (!this.room) return

      if (!this.screenTrack) {
        this.$mam.liveShareScreen()

        const stream = await navigator.mediaDevices.getDisplayMedia()
        this.screenTrack = new LocalVideoTrack(stream.getTracks()[0])

        this.room.localParticipant.videoTracks.forEach(publication => {
          publication.track.stop()
          publication.unpublish()
          this.disabledTrack(this.room.localParticipant, publication.track)
          this.commitToggleDevice('video', false)
        })

        this.room.localParticipant.publishTrack(this.screenTrack)
        this.screenTrack.mediaStreamTrack.onended = () => {
          this.shareScreenHandler()
        }
      } else {
        this.room.localParticipant.unpublishTrack(this.screenTrack)
        this.screenTrack.stop()
        this.screenTrack = null

        createLocalVideoTrack().then(localVideoTrack => {
          this.commitToggleDevice('video', true)
          this.enableTrack(this.room.localParticipant, localVideoTrack)
          return this.room.localParticipant.publishTrack(localVideoTrack)
        })
      }
    },

    disconnect() {
      if (this.room) {
        this.room.disconnect()
        this.room = null
        this.activeParticipant = null
        this.screenTrack = null
      }

      this.$emit('call-end')
    }
  }
}
</script>

<style lang="sass">
.container-video
  position: relative
  width: 100%
  overflow: hidden
  background-color: rgba(0, 0, 0, .8)
  height: calc(100vh - 64px)
  height: calc(var(--vh, 1vh) * 100 - 64px)
  @media screen and (max-width: 960px)
    height: calc(100vh - 56px)
    height: calc(var(--vh, 1vh) * 100 - 56px)

#main
  width: calc(100% - 300px)
  height: 100%
  @media screen and (max-width: 850px)
    width: 100%

.btn-block-video
  position: absolute
  width: calc(100% - 300px)
  height: 85px
  bottom: 0
  display: flex
  justify-content: space-between
  align-items: center
  background-color: rgba(0, 0, 0, .3)
  @media screen and (max-width: 850px)
    width: 100%
  @media screen and (max-width: 700px)
    justify-content: center

.side-btn-block
  max-width: 200px
  width: 100%
  height: 100%
  display: flex
  justify-content: center
  align-items: center
  overflow: hidden
  @media screen and (max-width: 700px)
    display: none

.side-btn-block-right
  display: flex
  justify-content: center
  align-items: center
  @media screen and (max-width: 700px)
    display: none

.room-name
  font-family: 'Fira Sans', sans-serif
  font-size: 16px
  text-align: left
  color: rgba(255, 255, 255, .87)

.share-btn
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center

.share-btn-label
  font-family: 'Fira Sans', sans-serif
  text-align: center
  font-size: 14px
  display: inline-block
  color: rgba(255, 255, 255, .87)

#participants
  position: absolute
  z-index: 50
  right: 0
  top: 0
  height: 100%
  max-width: 300px
  width: 100%
  overflow-y: scroll
  background-color: #F0F0F0
  transition: .3s all ease-in-out
  @media screen and (max-width: 850px)
    right: -300px
  video
    display: block

.participant
  position: relative
  background-color: #000
  height: 200px
  overflow: hidden

.open-participants-header
  position: absolute
  top: 10px
  right: 10px
  display: none
  @media screen and (max-width: 850px)
    display: block

.header-participants-controls
  margin: 10px
  display: flex
  justify-content: flex-end

.participant > .camera_avatar
  position: absolute
  color: white
  width: 40px
  top: 50%
  left: 50%
  transform: translate(-50%, -50%)

.mic_avatar
  position: absolute
  width: 30px
  bottom: 10px
  left: 10px
  background-color: red
  border-radius: 50px
  padding: 3px

.active-video
  width: 100%
  height: 100%

.active
  border-left: 6px solid var(--v-primary-base)

.pinned
  border: 1px solid limegreen

.btn-block
  display: flex
  justify-content: center
  flex-wrap: wrap

#localThumbnailPreview
  position: absolute
  display: flex
  justify-content: center
  left: 10px
  top: 10px
  width: 100px
  height: 120px
  overflow: hidden
  @media screen and (min-width: 600px)
    display: none
  video
    width: auto
    height: 100%
    border-radius: 20px

.title-room
  font-size: 28px
  color: rgba(0, 0, 0, .87)

.formWrongCode
  text-align: center
  max-width: 800px
  padding: 20px
  margin: 40px auto
  font-weight: 300

.centerLoader
  justify-content: center
  display: flex
  margin-top: 50px

.disable-mobile
  @media screen and (max-width: 600px)
    display: none

video
  display: block

.truncate-overflow
  overflow: hidden
  display: -webkit-box
  -webkit-line-clamp: 3
  -webkit-box-orient: vertical
</style>
